export interface User {
  id: string
  poetryNo: string
  nickname: string
  avatarColor: string
  bio: string
  phone?: string
  /** 自定义头像地址（形如 /api/uploads/<id>），空串表示使用首字母色块 */
  avatarUrl: string
}

export interface AuthSession {
  token: string
  sessionId: string
  expiresAt: string
  user: User
}

export interface FriendRequest {
  id: string
  createdAt: string
  user: User
}

/** Stable event ID is shared by realtime, APNs and polling to prevent duplicate alerts. */
export interface FriendNotice {
  id: string
  kind: 'request' | 'accepted'
  user: User
}

export function isFriendNotice(value: unknown): value is FriendNotice {
  if (!value || typeof value !== 'object') return false
  const notice = value as Partial<FriendNotice>
  return typeof notice.id === 'string' && !!notice.id && (notice.kind === 'request' || notice.kind === 'accepted')
    && !!notice.user && ['id', 'nickname', 'poetryNo', 'bio', 'avatarColor', 'avatarUrl'].every((key) =>
      typeof (notice.user as unknown as Record<string, unknown>)[key] === 'string')
}

export interface SearchUser extends User {
  relation: 'none' | 'friend' | 'incoming' | 'outgoing'
}

export interface Group {
  id: string
  name: string
  ownerId: string
  memberCount: number
  createdAt: string
  /** 群头像地址（形如 /api/uploads/<id>），空串表示使用群名首字母色块 */
  avatarUrl: string
  /** 群公告，空串表示暂无公告 */
  announcement: string
}

export interface GroupMember extends User {
  role: 'owner' | 'admin' | 'member'
  joinedAt: string
  /** 我在本群的昵称；仅本人有值（其他人的服务端已置空） */
  alias: string
}

/** 群二维码邀请预览（GET /api/groups/join/:token）返回的群信息子集。 */
export interface GroupJoinPreview {
  id: string
  name: string
  avatarUrl: string
  memberCount: number
}

/** 个人二维码（加好友）预览（GET /api/friends/qr/:token）。 */
export interface FriendQrPreview {
  user: User
  self: boolean
  alreadyFriend: boolean
}

export interface Message {
  id: string
  clientId: string
  conversationType: 'direct' | 'group'
  senderId: string
  recipientId: string | null
  groupId: string | null
  type: 'text' | 'poem'
  body: string
  createdAt: string
  recalledAt: string | null
  /** 服务端消息行号（用于已读回执定位） */
  messageRowid?: number | null
  /** 我发出的消息是否已被对方读过（仅一对一） */
  read?: boolean
  /** 引用回复的原消息快照；原消息撤回后清除引用，不影响回复正文。 */
  replyTo?: { id: string; senderNickname: string; preview: string } | null
  /** 群消息里发送者在本群的昵称（群昵称），没有则为空 */
  senderAlias?: string | null
  /** 服务端为群消息补充的发送者昵称。 */
  senderNickname?: string
}

/** 聊天记录搜索命中项（/api/messages/search 返回值，按会话过滤）。 */
export interface MessageSearchHit {
  id?: string
  conversationType?: 'direct' | 'group'
  senderId: string
  senderNickname: string
  senderAlias?: string | null
  createdAt: string
  type: 'text' | 'poem'
  body: string
  recalledAt?: string | null
}

export interface DeviceSession {
  id: string
  deviceName: string
  createdAt: string
  lastSeenAt: string
  expiresAt: string
  current: boolean
}

export type ChatTarget = { type: 'direct'; user: User } | { type: 'group'; group: Group }

interface ConversationBase {
  targetId: string
  lastMessage: Message | null
  unreadCount: number
  updatedAt: string
}

export type Conversation = ConversationBase & (
  { type: 'direct'; peerId: string; user: User } |
  { type: 'group'; groupId: string; group: Group }
)

export interface Poem {
  id: string
  title: string
  author: string
  dynasty: string
  content: string
  excerpt: string
  tag: string
}

export interface PoemCollection {
  id: string
  name: string
  poemIds: string[]
  createdAt: string
}

export interface PoemNote {
  id: string
  poemId: string
  body: string
  createdAt: string
}

export interface PoemTag {
  poemId: string
  tag: string
}

export interface ReadingState {
  savedPoemIds: string[]
  readPoemIds: string[]
  checkInDates: string[]
  collections: PoemCollection[]
  notes: PoemNote[]
  tags: PoemTag[]
}

/** 诗圈动态下的一条评论。 */
export interface MomentComment {
  id: string
  author: User
  body: string
  createdAt: string
}

/** 诗圈动态（朋友圈式时间线）。 */
export interface Moment {
  id: string
  author: User
  body: string
  /** 配图（站内 /api/uploads/... 地址，最多 9 张） */
  images: string[]
  /** 关联的诗（可为空） */
  poem: Poem | null
  createdAt: string
  likeCount: number
  likedByMe: boolean
  comments: MomentComment[]
}

/** 互动通知（点赞 / 评论我的动态）。 */
export interface AppNotification {
  id: string
  type: 'like' | 'comment'
  actor: User
  momentId: string | null
  preview: string
  createdAt: string
  read: boolean
}
